import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAcceptablePassword } from '@il-colpo/shared/account';
import { getAccountStore } from '../../account/accountStore.ts';
import type { AccountClient } from '../../account/types.ts';
import { Button, Panel, ScreenFrame, TextField } from '../../ui/index.ts';
import styles from './Account.module.css';
import { AuthPanel } from './AuthPanel.tsx';
import { DemoMailbox } from './DemoMailbox.tsx';
import { FormError, FormNote, Page, useAccountSession, useAction } from './common.tsx';
import { Overview } from './Overview.tsx';

function SecondFactorPanel({ client }: { client: AccountClient }) {
  const { t } = useTranslation('account');
  const [code, setCode] = useState('');
  const { pending, error, run } = useAction();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    await run(() => client.verifySecondFactor(code));
  };

  return (
    <Panel title={t('auth.secondFactorTitle')}>
      <form className={styles['form']} onSubmit={(e) => void submit(e)} noValidate>
        <p className={styles['note']}>{t('auth.secondFactorBody')}</p>
        <TextField
          label={t('auth.code')}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(v) => {
            setCode(v.replace(/\D/g, '').slice(0, 6));
          }}
        />
        <FormError code={error} />
        <Button type="submit" loading={pending} disabled={code.length !== 6} block>
          {t('auth.secondFactorVerify')}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            void getAccountStore().getState().signOut({ everywhere: false, wipeDevice: false });
          }}
        >
          {t('auth.secondFactorOut')}
        </Button>
      </form>
    </Panel>
  );
}

/** After a recovery code the player is signed in and chooses a new password before going on. */
function NewPasswordPanel({ client, onDone }: { client: AccountClient; onDone: () => void }) {
  const { t } = useTranslation('account');
  const [password, setPassword] = useState('');
  const [saved, setSaved] = useState(false);
  const { pending, error, run } = useAction();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const r = await run(() => client.setPassword(password));
    if (r.ok) {
      setSaved(true);
      onDone();
    }
  };

  return (
    <Panel title={t('auth.resetTitle')}>
      <form className={styles['form']} onSubmit={(e) => void submit(e)} noValidate>
        <TextField
          label={t('auth.newPassword')}
          type="password"
          autoComplete="new-password"
          hint={t('auth.passwordHint')}
          value={password}
          onChange={setPassword}
        />
        <FormError code={error} />
        {saved ? <FormNote>{t('auth.passwordSaved')}</FormNote> : null}
        <Button type="submit" loading={pending} disabled={!isAcceptablePassword(password)} block>
          {t('auth.setPassword')}
        </Button>
      </form>
    </Panel>
  );
}

export function AccountScreen() {
  const { t } = useTranslation('account');
  const { available, mode, load, snapshot, client } = useAccountSession();
  const [recovering, setRecovering] = useState(false);
  const retry = () => {
    void getAccountStore().getState().ensure();
  };

  let body;
  if (!available) {
    body = (
      <Panel title={t('unavailable.title')}>
        <p className={styles['note']}>{t('unavailable.body')}</p>
      </Panel>
    );
  } else if (load === 'failed') {
    body = (
      <Panel title={t('loadFailed.title')}>
        <div className={styles['stack']}>
          <p className={styles['note']}>{t('loadFailed.body')}</p>
          <div>
            <Button onClick={retry}>{t('loadFailed.retry')}</Button>
          </div>
        </div>
      </Panel>
    );
  } else if (!client || load !== 'ready' || snapshot.status === 'loading') {
    body = (
      <Panel>
        <p className={styles['note']} role="status">
          {t('loading')}
        </p>
      </Panel>
    );
  } else if (snapshot.status === 'signedOut') {
    body = (
      <AuthPanel
        client={client}
        onRecovered={() => {
          setRecovering(true);
        }}
      />
    );
  } else if (snapshot.needsSecondFactor) {
    body = <SecondFactorPanel client={client} />;
  } else if (recovering) {
    body = (
      <NewPasswordPanel
        client={client}
        onDone={() => {
          setRecovering(false);
        }}
      />
    );
  } else {
    body = <Overview />;
  }

  return (
    <ScreenFrame title={t('title')} backTo="/">
      <Page>
        {mode === 'demo' ? (
          <>
            <Panel tone="lamp">
              <p className={styles['note']}>{t('demo.banner')}</p>
            </Panel>
            <DemoMailbox />
          </>
        ) : null}
        {body}
      </Page>
    </ScreenFrame>
  );
}
